import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { UserId } from "@visioni/a3s-users";
import { setUserPermissionsMutation } from "../mutations";
import { accessManagementQuery } from "../queries";
import { CameraId } from "../../schemas";
import type { CameraRole, Role } from "../../schemas";

export interface SetUserPermissionsInput {
  user: string;
  roles: Role[];
  cameraPermissions: Array<{ camera: string; role: CameraRole }>;
}

/**
 * Hook to declaratively set a user's full permissions (roles + camera grants)
 * within the current tenant. The backend diffs against the current state and
 * applies adds/removes atomically.
 *
 * Accepts plain string `user` IDs — parsing to `UserId` is handled internally.
 * Automatically invalidates the access management query on success.
 */
export function useSetUserPermissions() {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationApiConfig = {
    tenantId: apiConfig.tenant?.tenantId,
    apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
    authenticatedFetch: apiConfig.authenticatedFetch,
  };

  const base = setUserPermissionsMutation(mutationApiConfig, {});

  return useMutation({
    ...base,
    mutationFn: (input: SetUserPermissionsInput) =>
      base.mutationFn({
        user: UserId.fromString(input.user),
        roles: input.roles,
        cameraPermissions: input.cameraPermissions.map((cp) => ({
          camera: CameraId.parse(cp.camera),
          role: cp.role,
        })),
      }),
    onSuccess: () => {
      const queryKey = accessManagementQuery(apiConfig, {}).queryKey;
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
